import { useMemo } from 'react';
import {
  DynamicFieldProperty_Enum,
  Template,
  TemplateType_Slug,
} from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { RelatedType } from '@weave/schema-gen-ts/dist/shared/sms/v1/enums.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { CustomizationFlagQueries, CustomizationFlagTypes } from '@frontend/api-customization-flags';
import { ReviewInvitationMutations } from '@frontend/api-reviews-invitation';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { ManualTemplateTypes, useGetDefaultTemplate, UseTemplateFlowResult } from '@frontend/integrated-messaging';
import { useAppScopeStore } from '@frontend/scope';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { useAlert } from '@frontend/design-system';
import { UseTemplateFlowArgs } from '../types';

export const useReviewInvitationTemplateFlow = ({
  groupId,
  personId,
  renderTemplate,
  modalControl,
}: UseTemplateFlowArgs): UseTemplateFlowResult => {
  const { t } = useTranslation('inbox-templates');
  const alert = useAlert();
  const { accessibleLocationData } = useAppScopeStore();
  const isVet = accessibleLocationData[groupId]?.vertical === Vertical.VET;
  const templateTypes = useMemo(() => {
    const result: ManualTemplateTypes[] = [TemplateType_Slug.MANUAL_REVIEW_REQUEST];
    if (isVet) result.push(TemplateType_Slug.MANUAL_REVIEW_REQUEST_PET);
    return result;
  }, [isVet]);
  const getDefaultTemplate = useGetDefaultTemplate({ groupIds: [groupId] });
  const lastUsedReviewTemplate = getDefaultTemplate({
    type: isVet ? TemplateType_Slug.MANUAL_REVIEW_REQUEST_PET : TemplateType_Slug.MANUAL_REVIEW_REQUEST,
  });
  const hasReviewsAcl = hasSchemaACL(groupId, Permission.REVIEW_READ);
  const reviewsCustomization = CustomizationFlagQueries.useAggregateCustomizationFlagDetails({
    locationIds: [groupId],
    customizationFlag: Feature.REVIEWS_HOME_ICON,
    activeStates: [CustomizationFlagTypes.CustomizationFlagState.ACTIVE],
  }).isFeatureActiveInAllLocations;

  const handleReviewTemplateClick = (template?: Template) => {
    if (!template) return;

    reviewInvitationMutation.mutate(
      {
        locationId: groupId,
        personId,
        ...(!template.templateId.includes('static') && { templateId: template.templateId }),
      },
      {
        onSuccess: (data) => {
          renderTemplate({
            template,
            linkData: [
              {
                link: data.invitation.invitationLink,
                relatedId: data.invitation.invitationId,
                relatedType: RelatedType.RELATED_TYPE_REVIEW,
              },
            ],
            additionalBindings: {
              [DynamicFieldProperty_Enum.REVIEW_REQUEST_LINK]: data.invitation.invitationLink,
            },
          });
          modalControl.closeModal();
        },
        onError: () => {
          alert.error(t('An error occurred generating the review invitation link. Please try again later.'));
        },
      }
    );
  };

  const reviewInvitationMutation = ReviewInvitationMutations.useReviewInvitationMutation();

  return {
    popoverItem:
      hasReviewsAcl && reviewsCustomization
        ? {
            iconName: 'star',
            label: t('Review Request Template'),
            trackingId: `${InboxPrefixes.Thread}-request-review-button`,
            onClick: () => handleReviewTemplateClick(lastUsedReviewTemplate),
          }
        : undefined,
    tabItem: {
      tabLabel: t('Review'),
      templateSelectorProps: {
        groupIds: [groupId],
        templateTypes,
        staticDefaultTemplateTypes: isVet
          ? [TemplateType_Slug.MANUAL_REVIEW_REQUEST_PET]
          : [TemplateType_Slug.MANUAL_REVIEW_REQUEST],
        onSelectTemplate: (template) => {
          handleReviewTemplateClick(template);
        },
      },
    },
  };
};
